import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { store } from '../app/store';
import { setAccessToken, logout } from '../features/auth/authSlice';

/** API base URL: relative in dev (Vite proxy), absolute deployed URL in prod. */
const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * Axios instance with auth header injection and silent refresh on 401.
 * Access token is kept in memory (Redux); refresh token is an httpOnly cookie.
 */
export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // send refresh cookie
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = store.getState().auth.accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshing: Promise<string | null> | null = null;

/**
 * Single-flight access-token refresh. Concurrent callers (REST interceptor and
 * the WS gateway) share one in-flight refresh instead of stampeding /auth/refresh.
 */
export function refreshSession(): Promise<string | null> {
  refreshing ??= refreshAccessToken().finally(() => { refreshing = null; });
  return refreshing;
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const code = (error.response?.data as { error?: { code?: string } })?.error?.code;

    if (error.response?.status === 401 && code === 'TOKEN_EXPIRED' && !original._retry) {
      original._retry = true;
      const newToken = await refreshSession();
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
      store.dispatch(logout());
    } else if (error.response?.status === 401 && store.getState().auth.accessToken) {
      // Session invalidated server-side (revoked, or signed in from another device) → end this one.
      store.dispatch(logout());
    }
    return Promise.reject(error);
  },
);

async function refreshAccessToken(): Promise<string | null> {
  try {
    const res = await axios.post(`${API_URL}/auth/refresh`, {}, { withCredentials: true });
    const token = res.data.data.accessToken as string;
    store.dispatch(setAccessToken(token));
    return token;
  } catch {
    return null;
  }
}
